import {
	CollectionModelFieldHeight,
	CollectionModelFieldSubFormInstance,
	IkePoleForemanFieldIds,
	isIPFSpanType,
} from '@ikegps/core-shared';
import { IkeCalcFieldJoin } from './field.join.ikecalc';

const POWER_CIRCUIT_PHASE_HEIGHTS: string[] = [
	IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_PHASE_A_HEIGHT,
	IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_PHASE_B_HEIGHT,
	IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_PHASE_C_HEIGHT,
	IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_PHASE_A_HEIGHT,
	IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_PHASE_B_HEIGHT,
	IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_PHASE_C_HEIGHT,
	IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SHIELD_WIRE_HEIGHT,
	IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_NEUTRAL_HEIGHT,
];

export class PoleForemanPowerCircuitPhaseHeightFieldJoin extends IkeCalcFieldJoin {
	type = 'PoleForemanPowerCircuitPhaseHeightJoin';

	static accepts(field: CollectionModelFieldHeight) {
		return POWER_CIRCUIT_PHASE_HEIGHTS.indexOf(field.schema.id) > -1;
	}

	constructor(field: CollectionModelFieldHeight) {
		super(field);
	}

	/**
	 * Check if this field can join with another field by checking if they match framing and conductor types.
	 * @param other - The other field to check.
	 * @returns True if the field is a power circuit phase height field and matches the other field's framing and conductor types.
	 */
	joins(other: PoleForemanPowerCircuitPhaseHeightFieldJoin): boolean {
		const joinableHeight = this.getFieldValue(this.circuit, this.field.schema.id);
		if (joinableHeight != null) {
			return false;
		}

		// Determine which field type this is
		const id = this.field.schema.id;
		const isPrimary = id.startsWith('ipf_spanPowerCircuitPrimary');
		const isSecondary = id.startsWith('ipf_spanPowerCircuitSecondary');
		const isShieldWire = id === IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SHIELD_WIRE_HEIGHT;
		const isNeutral = id === IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_NEUTRAL_HEIGHT;

		// Use correct framing/conductor fields for matching
		let framingField = '';
		let conductorField = '';
		if (isPrimary) {
			framingField = IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_FRAMING;
			conductorField = IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_PRIMARY_CONDUCTOR;
		} else if (isSecondary) {
			framingField = IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_FRAMING;
			conductorField = IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SECONDARY_CONDUCTOR;
		} else if (isShieldWire) {
			framingField = IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SHIELD_WIRE_FRAMING;
			conductorField = IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_SHIELD_WIRE_CONDUCTOR;
		} else if (isNeutral) {
			framingField = IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_NEUTRAL_FRAMING;
			conductorField = IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_NEUTRAL_CONDUCTOR;
		} else {
			return false;
		}

		const framingType = this.getFieldValue(this.circuit, framingField);
		if (framingType && this.isDeadEnd(framingType)) {
			return false;
		}

		if (!other.circuit.hasField(id) || this.getFieldValue(other.circuit, id) != null) {
			return false;
		}

		return (
			this.spansOpposing(this.span, other.span) &&
			this.fieldsEqual(this.circuit, other.circuit, framingField) &&
			this.fieldsEqual(this.circuit, other.circuit, conductorField) &&
			this.fieldsEqual(this.circuit, other.circuit, IkePoleForemanFieldIds.SPAN_POWER_CIRCUIT_TYPE)
		);
	}

	// Check if the spans are opposing (i.e., one is a fore span and the other is a back span)
	protected override spansOpposing(
		spanA: CollectionModelFieldSubFormInstance,
		spanB: CollectionModelFieldSubFormInstance
	): boolean {
		if (
			spanA.instanceId === spanB.instanceId ||
			!spanA.hasField(IkePoleForemanFieldIds.SPAN_TYPE) ||
			!spanB.hasField(IkePoleForemanFieldIds.SPAN_TYPE)
		) {
			return false;
		}

		const spanAType = spanA.getField(IkePoleForemanFieldIds.SPAN_TYPE).value;
		const spanBType = spanB.getField(IkePoleForemanFieldIds.SPAN_TYPE).value;

		if (!spanAType || !spanBType) {
			return false;
		}

		if (isIPFSpanType(spanAType) && isIPFSpanType(spanBType) && spanAType !== spanBType) {
			return true;
		}

		return false;
	}
}
